import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  provisionalGradeScalesApi,
  type ProvisionalGradeScale,
  type ProvisionalBand,
} from "../../../service/provisional-grade-scale"

// Provisional Grade Scales — a named percentage → letter scale with bands
// (inclusive both ends, sorted top-to-bottom). Mirrors the RMS page: a scale
// selector + New scale, a Name, an editable Bands table, Save, Delete.
const NEW = "__new__"

const STARTER_BANDS: ProvisionalBand[] = [
  { minPercentage: 80, maxPercentage: 100, letter: "A+", label: "Outstanding" },
  { minPercentage: 70, maxPercentage: 79.99, letter: "A", label: "Excellent" },
  { minPercentage: 60, maxPercentage: 69.99, letter: "B", label: "Very Good" },
  { minPercentage: 50, maxPercentage: 59.99, letter: "C", label: "Good" },
  { minPercentage: 40, maxPercentage: 49.99, letter: "D", label: "Satisfactory" },
  { minPercentage: 33, maxPercentage: 39.99, letter: "E", label: "Pass" },
  { minPercentage: 0, maxPercentage: 32.99, letter: "U", label: "Unsatisfactory" },
]

type BandDraft = { minPercentage: string; maxPercentage: string; letter: string; label: string }

const toDraft = (b: ProvisionalBand): BandDraft => ({
  minPercentage: String(b.minPercentage),
  maxPercentage: String(b.maxPercentage),
  letter: b.letter || "",
  label: b.label || "",
})

const ProvisionalGradeScales = () => {
  const [scales, setScales] = useState<ProvisionalGradeScale[]>([])
  const [selected, setSelected] = useState<string>(NEW)
  const [name, setName] = useState("")
  const [bands, setBands] = useState<BandDraft[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const applyScale = (id: string, list: ProvisionalGradeScale[]) => {
    if (id === NEW) {
      setName("")
      setBands(STARTER_BANDS.map(toDraft))
      return
    }
    const s = list.find(x => x._id === id)
    setName(s?.name || "")
    setBands((s?.bands || []).map(toDraft))
  }

  const load = async (preferId?: string) => {
    setLoading(true)
    try {
      const res = await provisionalGradeScalesApi.list()
      const list = res.data || []
      setScales(list)
      const pick = preferId && list.some(s => s._id === preferId)
        ? preferId
        : list[0]?._id || NEW
      setSelected(pick)
      applyScale(pick, list)
    } catch {
      toast.error("Failed to load grade scales")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSelect = (id: string) => {
    setSelected(id)
    applyScale(id, scales)
  }

  const setBand = (i: number, patch: Partial<BandDraft>) =>
    setBands(cur => cur.map((b, idx) => (idx === i ? { ...b, ...patch } : b)))

  const addBand = () =>
    setBands(cur => [...cur, { minPercentage: "", maxPercentage: "", letter: "", label: "" }])

  const removeBand = (i: number) => setBands(cur => cur.filter((_, idx) => idx !== i))

  const save = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) { toast.error("Enter a scale name."); return }
    const parsed: ProvisionalBand[] = []
    for (const b of bands) {
      const min = Number(b.minPercentage)
      const max = Number(b.maxPercentage)
      const letter = b.letter.trim()
      if (!letter) { toast.error("Every band needs a letter."); return }
      if (!Number.isFinite(min) || !Number.isFinite(max)) { toast.error(`Band "${letter}" needs numeric MIN and MAX.`); return }
      if (min > max) { toast.error(`Band "${letter}": MIN cannot exceed MAX.`); return }
      parsed.push({ minPercentage: min, maxPercentage: max, letter, label: b.label.trim() })
    }
    if (!parsed.length) { toast.error("Add at least one band."); return }

    setSaving(true)
    try {
      if (selected === NEW) {
        const res = await provisionalGradeScalesApi.create({ name: trimmedName, bands: parsed })
        toast.success("Scale created")
        await load(res.data?._id)
      } else {
        await provisionalGradeScalesApi.update(selected, { name: trimmedName, bands: parsed })
        toast.success("Scale saved")
        await load(selected)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    if (selected === NEW) return
    setSaving(true)
    try {
      await provisionalGradeScalesApi.remove(selected)
      toast.success("Scale deleted")
      setConfirmDelete(false)
      await load()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Provisional Grade Scales</h3>
          <p className="text-muted mb-0">Named percentage → letter scales used as a simple grading fallback.</p>
        </div>

        {loading ? (
          <div className="text-center p-4"><span className="spinner-border text-primary" /></div>
        ) : (
          <div className="card">
            <div className="card-body">
              {/* Scale selector + New scale */}
              <div className="row g-3 align-items-end mb-3">
                <div className="col-md-6">
                  <label className="form-label">Scale</label>
                  <select className="form-select" value={selected} onChange={e => onSelect(e.target.value)}>
                    {scales.map(s => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                    <option value={NEW}>+ New scale…</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => onSelect(NEW)}
                    disabled={selected === NEW}
                  >
                    <i className="ti ti-plus me-1" /> New scale
                  </button>
                </div>
              </div>

              {/* Name */}
              <div className="mb-3" style={{ maxWidth: 640 }}>
                <label className="form-label">Name</label>
                <input
                  className="form-control"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Default Sindh SSC scale"
                />
              </div>

              {/* Bands */}
              <div className="d-flex align-items-center gap-2 mb-2">
                <h5 className="mb-0">Bands</h5>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addBand}>
                  <i className="ti ti-plus me-1" /> Add band
                </button>
                <span className="text-muted small">Inclusive on both ends. Sorted top-to-bottom by sort order.</span>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered align-middle mb-3">
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>MIN %</th>
                      <th style={{ width: 120 }}>MAX %</th>
                      <th style={{ width: 120 }}>LETTER</th>
                      <th>LABEL (OPTIONAL)</th>
                      <th style={{ width: 48 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {bands.length === 0 && (
                      <tr><td colSpan={5} className="text-center text-muted">No bands — click “Add band”.</td></tr>
                    )}
                    {bands.map((b, i) => (
                      <tr key={i}>
                        <td>
                          <input type="number" className="form-control" value={b.minPercentage}
                            onChange={e => setBand(i, { minPercentage: e.target.value })} />
                        </td>
                        <td>
                          <input type="number" className="form-control" value={b.maxPercentage}
                            onChange={e => setBand(i, { maxPercentage: e.target.value })} />
                        </td>
                        <td>
                          <input className="form-control" value={b.letter}
                            onChange={e => setBand(i, { letter: e.target.value })} />
                        </td>
                        <td>
                          <input className="form-control" value={b.label}
                            onChange={e => setBand(i, { label: e.target.value })} />
                        </td>
                        <td className="text-center">
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeBand(i)}>
                            <i className="ti ti-x" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                {selected !== NEW && (
                  confirmDelete ? (
                    <>
                      <button type="button" className="btn btn-danger" onClick={doDelete} disabled={saving}>
                        Confirm delete
                      </button>
                      <button type="button" className="btn btn-light" onClick={() => setConfirmDelete(false)} disabled={saving}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button type="button" className="btn btn-outline-danger" onClick={() => setConfirmDelete(true)} disabled={saving}>
                      Delete this scale
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProvisionalGradeScales
